import React, { FunctionComponent, useCallback } from "react";
import styles from "./Header4.module.scss";
import Header4Props, { AnchorMenuLink } from "./Header4Props.interface";

import getAnchorsFromBlocks from "./helpers/getAnchorsFromBlocks";
import MenuOverlay from "./components/MenuOverlay/MenuOverlay";
import setHeaderStyles from "./hooks/setHeaderStyles";
import SocialLinks from "./components/SocialLinks/SocialLinks";

import { CLOSE_ALL_OVERLAYS, OPEN_MENU } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import isBrowserContext from "helpers/isBrowserContext";
import useScrollState from "layout/Header/hooks/useScrollState";
import Anchor from "components/Anchor/Anchor";
import FixrOverlay from "../Header2/components/FixrOverlay/FixrOverlay";

const Header4: FunctionComponent<Header4Props> = (props) => {
    const dispatch = useDispatch();

    let header: any;
    if (props?.theme?.header?.[0] && props?.theme?.header.length > 0) {
        header = props?.theme?.header[0]?.content;
    }

    const headerStyles = setHeaderStyles(header);
    const scrollTypeClass = useScrollState(header?.scrollType, styles);

    const page = useSelector(state => state.page.content);

    const overlay = useSelector(state => state.overlay);
    const overlayActiveClass = overlay.currentOverlay === "MENU" ? styles.is_active : undefined;

    const blocks = useSelector(state => state.page?.content?.blocks);
    const anchors = getAnchorsFromBlocks(blocks);

    const logoTypeClass = header?.logoType ? styles[`logo_type_${header.logoType}`] : undefined;
    const logoImageType = header?.logoType === "Vertical" ? "containX" : "containY";
    const imageAspectRatio = getImageAspectRatio(undefined, header?.logo?.umbracoWidth, header?.logo?.umbracoHeight);

    const headerButtonContentType = header?.headerButtons?.[0] && header.headerButtons.length > 0 ? header.headerButtons[0].content._contentType : undefined;

    const toggleMenu = useCallback((event: any) => {
        if (event) {
            event.stopPropagation();
        }

        if (overlay?.currentOverlay === "MENU") {
            dispatch({
                type: CLOSE_ALL_OVERLAYS
            });
        }
        else {
            dispatch({
                type: OPEN_MENU
            });
        }
    }, [overlay.currentOverlay]);

    const onAnchorClick = (anchor: AnchorMenuLink) => {
        // TODO: Do Anchor slugify the same way everywhere.
        const identifier = (anchor.label).replace(/\s+/g, "-").toLowerCase();

        if (isBrowserContext()) {
            const blockElement = document.getElementById(identifier);

            if (blockElement && typeof (blockElement.offsetTop) !== "undefined") {
                window.scrollTo({
                    top: blockElement?.offsetTop - 100, // TODO: Minus 100 is hardcoded, should be dynamic
                    left: 0,
                    behavior: "smooth"
                });
            }
        }
    };

    return (
        <header className={combineClasses(styles.wrapper, scrollTypeClass)} style={headerStyles}>
            <div className={combineClasses(styles.inner, logoTypeClass)}>
                {header?.logo ? (
                    <Anchor className={styles.image_link} to={page?._homeNodeUrl}>
                        <ImageViewer className={styles.image} src={header.logo.umbracoFile.src} extensionType="png" type={logoImageType} aspectRatioX={imageAspectRatio.aspectRatioX} aspectRatioY={imageAspectRatio.aspectRatioY} />
                    </Anchor>
                ) : null}

                <div className={styles.content}>
                    <div className={styles.links}>
                        {anchors && anchors.map((anchor, index) => {
                            return (
                                <div key={index} className={styles.link} onClick={() => onAnchorClick(anchor)}>
                                    <span className={styles.link_adjustment}>
                                        {anchor.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <SocialLinks {...props} />

                    {/* Buttons */}
                    {props.headerButtons?.map((button, index) => {
                        return (
                            <Button key={index} to={button.content.link} className={styles.action_button} contentType={headerButtonContentType} {...button.content}>
                                {button.content.label}
                            </Button>
                        );
                    })}
                </div>

                <button className={combineClasses(styles.menu_toggle_button, overlayActiveClass)} onClick={(event) => toggleMenu(event)}>
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>

            {overlay.currentOverlay === "MENU" ? (
                <MenuOverlay header={header} anchors={anchors} {...props} />
            ) : null}

            {overlay.currentOverlay === "FIXR_OVERLAY" ? (
                <FixrOverlay {...props} state={overlay.currentOverlay} />
            ) : null}
        </header>
    );
};

export default Header4;